import { useState, useEffect, useCallback } from 'react';
import { subscribeToUserChannel, ProgressEvent, ExportReadyEvent } from '../../lib/echo';

export interface AppNotification {
  id: string;
  title: string;
  body: string;
  type: 'success' | 'error' | 'export';
  timestamp: Date;
  read: boolean;
  taskId?: string;
  taskType?: 'single' | 'bulk';
}

export function useNotifications(userId: string | undefined) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  const add = useCallback((n: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
    setNotifications(prev => [
      { ...n, id: Date.now().toString(), timestamp: new Date(), read: false },
      ...prev.slice(0, 19),
    ]);
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const clear = useCallback(() => setNotifications([]), []);

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToUserChannel(
      userId,
      (e: ProgressEvent) => {
        if (e.status === 'completed') {
          const label = e.task_type === 'bulk' ? 'Bulk batch' : 'Search';
          add({
            title: `${label} complete`,
            body: `${e.company || 'Job'} finished, ${e.leads_found ?? 0} leads found.`,
            type: 'success',
            taskId: e.task_id,
            taskType: e.task_type,
          });
        } else if (e.status === 'failed') {
          add({
            title: 'Job failed',
            body: `${e.company || 'Job'} encountered an error.`,
            type: 'error',
            taskId: e.task_id,
            taskType: e.task_type,
          });
        }
      },
      (e: ExportReadyEvent) => {
        // Notify SearchHistoryDetail if it's open for this task
        window.dispatchEvent(new CustomEvent('export:ready', { detail: e }));
        add({
          title: 'Export Ready',
          body: `${e.company}, ${(e.row_count ?? 0).toLocaleString()} rows ready to download.`,
          type: 'export',
          taskId: e.task_id,
        });
      }
    );

    return unsubscribe;
  }, [userId, add]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return { notifications, unreadCount, markAllRead, clear };
}
